// src/pages/RosterImport.js
// Imports the source-system Roster .xlsx (e.g. Roster_2026-05-20_09.02pm.xlsx)
// into Firestore. Active + Terminated only. Two-pass write so managerId
// references resolve correctly.
//
// Source columns expected:
//   Name | Email | State | Status | Active Date | Term Date | WorkMarket |
//   Role | Cgb Max Hold | Comp | RM | Recruiter | Recruiting Assistant |
//   Director | Manager | Lead | Project Manager
//
// Strategy:
//  1. Parse + clean (strip U+200C zero-width chars from every cell)
//  2. Filter Status in [Active, Terminated]
//  3. Match by email against existing users
//  4. Bucket: matched (will update) / new (will create) / skipped (no email)
//  5. Pass A: upsert all reps (name, email, role, status, dates, compPlanId).
//             New reps get needsAgentCode: true and a random temp password.
//  6. Pass B: resolve each rep's manager — prefer most-immediate upline
//             (Lead > Manager > Director > Recruiting Assistant > Recruiter > RM)
//             — look up that name in the just-built name->uid map, write managerId.
import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import * as XLSX from 'xlsx';

// Generate a UID that mirrors Firebase Auth UID format (28-char base62).
// Used as a placeholder until a real auth account is provisioned for the rep.
function genUid() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let s = '';
  for (let i = 0; i < 28; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}

// Strip zero-width chars and trim.
function clean(v) {
  if (v == null) return '';
  return String(v).replace(/\u200c/g, '').trim();
}

// Source-system role label -> CRM role.
const ROLE_MAP = {
  'Agent': 'Agent',
  'RM': 'Regional',
  'Director': 'Director',
  'Manager': 'Manager',
  'Lead': 'Lead',
  'Recruiter': 'Recruiter',
  'Recruiting Assistant': 'Asst Recruiter',
  'Project Manager': 'Manager',
  'Administrator': 'admin',
  'Director, Administrator': 'admin',
  'Recruiter, Administrator': 'admin',
  'Recruiting Assistant, Administrator': 'admin',
};

// When resolving managerId, we prefer the most-immediate upline.
// Source columns from closest to furthest:
const UPLINE_COLUMNS = ['Lead', 'Manager', 'Recruiting Assistant', 'Recruiter', 'Director', 'RM'];

// Source roster column → comp plan role key (matches compPlans.js ROLE_KEYS).
// Each agent stores their named uplines so commission calc can pay the
// correct person per role, even when the upline is on a different comp plan
// (e.g. a recruiter who recruits across teams).
const COLUMN_TO_ROLE = {
  'Lead': 'Lead',
  'Manager': 'Manager',
  'Recruiting Assistant': 'AsstRecruiter',
  'Recruiter': 'Recruiter',
  'Director': 'Director',
  'RM': 'Regional',
};

// Build a lowercase->target lookup from name fragments to handle minor differences.
function nameKey(s) {
  return clean(s).toLowerCase().replace(/\s+/g, ' ');
}

export default function RosterImport() {
  const [compPlans, setCompPlans] = useState([]);
  const [existingUsers, setExistingUsers] = useState([]);
  const [parsedRows, setParsedRows] = useState(null);
  const [buckets, setBuckets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ stage: '', done: 0, total: 0, errors: [] });
  const [doneSummary, setDoneSummary] = useState(null);

  useEffect(() => {
    async function load() {
      const [planSnap, userSnap] = await Promise.all([
        getDocs(collection(db, 'compPlans')),
        getDocs(collection(db, 'users')),
      ]);
      setCompPlans(planSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setExistingUsers(userSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }
    load();
  }, []);

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const wb = XLSX.read(new Uint8Array(ev.target.result), { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

      // Find header row (skip the "Roster ..." title row)
      let headerIdx = 0;
      for (let i = 0; i < Math.min(5, aoa.length); i++) {
        if (aoa[i].some(c => clean(c).toLowerCase() === 'email')) {
          headerIdx = i;
          break;
        }
      }
      const headers = aoa[headerIdx].map(clean);
      const rows = [];
      for (let i = headerIdx + 1; i < aoa.length; i++) {
        const obj = {};
        headers.forEach((h, j) => { obj[h] = clean(aoa[i][j]); });
        if (!obj.Email) continue;
        rows.push(obj);
      }
      setParsedRows(rows);
      buildBuckets(rows);
    };
    reader.readAsArrayBuffer(file);
  }

  function buildBuckets(rows) {
    const existingByEmail = new Map();
    existingUsers.forEach(u => {
      if (u.email) existingByEmail.set(u.email.toLowerCase(), u);
    });
    const planByName = new Map();
    compPlans.forEach(p => { if (p.name) planByName.set(p.name.toUpperCase(), p.id); });

    const matched = [];
    const newRows = [];
    const skipped = [];

    for (const r of rows) {
      const status = r.Status;
      if (status !== 'Active' && status !== 'Terminated') {
        skipped.push({ ...r, _reason: `status=${status}` });
        continue;
      }
      if (!r.Email.includes('@')) {
        skipped.push({ ...r, _reason: 'invalid email' });
        continue;
      }
      const compId = r.Comp ? (planByName.get(r.Comp.toUpperCase()) || null) : null;
      const role = ROLE_MAP[r.Role] || 'Agent';
      const existing = existingByEmail.get(r.Email.toLowerCase());
      const enriched = { ...r, _role: role, _compPlanId: compId, _compMissing: !!(r.Comp && !compId) };
      if (existing) matched.push({ ...enriched, _existing: existing });
      else newRows.push(enriched);
    }

    setBuckets({ matched, newRows, skipped });
  }

  async function runImport() {
    if (!buckets) return;
    setRunning(true);
    setDoneSummary(null);
    const errors = [];

    // ─── PASS A — upsert reps ───────────────────────────────────────
    setProgress({ stage: 'Writing reps', done: 0, total: buckets.matched.length + buckets.newRows.length, errors: [] });
    const nameToUid = new Map();        // "greg ours" -> uid
    const nameToDocId = new Map();      // for in-session updates
    const writtenReps = [];             // {docId, uid, row}
    let done = 0;

    // existing users first (also populate name map so they can be referenced as managers)
    existingUsers.forEach(u => {
      const key = nameKey(u.name);
      if (key) {
        nameToUid.set(key, u.uid || u.id);
        nameToDocId.set(key, u.id);
      }
    });

    for (const r of buckets.matched) {
      try {
        const updates = {
          name: r.Name,
          role: r._role,
          status: r.Status,
          active: r.Status === 'Active',
          activeDate: r['Active Date'] || null,
          terminationDate: r['Term Date'] || null,
        };
        if (r._compPlanId) updates.compPlanId = r._compPlanId;
        await updateDoc(doc(db, 'users', r._existing.id), updates);
        const uid = r._existing.uid || r._existing.id;
        nameToUid.set(nameKey(r.Name), uid);
        nameToDocId.set(nameKey(r.Name), r._existing.id);
        writtenReps.push({ docId: r._existing.id, uid, row: r });
      } catch (err) {
        errors.push({ name: r.Name, email: r.Email, error: `update failed: ${err.message}` });
      }
      done++;
      setProgress(p => ({ ...p, done }));
    }

    for (const r of buckets.newRows) {
      try {
        // No Auth account yet — placeholder UID so downline/payroll/chargebacks
        // can resolve. Real auth account can be provisioned later via password
        // reset when the rep actually needs to log in.
        const newUid = genUid();
        const profile = {
          name: r.Name,
          email: r.Email.toLowerCase(),
          role: r._role,
          status: r.Status,
          active: r.Status === 'Active',
          activeDate: r['Active Date'] || null,
          terminationDate: r['Term Date'] || null,
          uid: newUid,
          compPlanId: r._compPlanId || null,
          agentCode: '',
          needsAgentCode: true,
          needsAuthAccount: true,
          phone: '',
        };
        const ref = await addDoc(collection(db, 'users'), profile);
        nameToUid.set(nameKey(r.Name), newUid);
        nameToDocId.set(nameKey(r.Name), ref.id);
        writtenReps.push({ docId: ref.id, uid: newUid, row: r });
      } catch (err) {
        errors.push({ name: r.Name, email: r.Email, error: `create failed: ${err.message}` });
      }
      done++;
      setProgress(p => ({ ...p, done, errors }));
    }

    // ─── PASS B — resolve managerId ─────────────────────────────────
    setProgress({ stage: 'Linking uplines', done: 0, total: writtenReps.length, errors });
    let linked = 0;
    let unresolved = 0;
    for (const w of writtenReps) {
      const uplines = {};
      let managerUid = null;       // priority-collapsed direct supervisor (for downline)
      let managerName = null;
      for (const col of UPLINE_COLUMNS) {
        const candidate = w.row[col];
        if (!candidate) continue;
        // skip self-reference (e.g. a Manager row that lists themselves in the Manager column)
        if (nameKey(candidate) === nameKey(w.row.Name)) continue;
        const uid = nameToUid.get(nameKey(candidate));
        if (!uid) continue;
        const roleKey = COLUMN_TO_ROLE[col];
        if (roleKey && !uplines[roleKey]) {
          uplines[roleKey] = { id: uid, name: candidate };
        }
        if (!managerUid) {
          managerUid = uid;
          managerName = candidate;
        }
      }
      if (managerUid || Object.keys(uplines).length > 0) {
        try {
          await updateDoc(doc(db, 'users', w.docId), {
            uplines,
            managerId: managerUid,
            managerName,
          });
          linked++;
        } catch (err) {
          errors.push({ name: w.row.Name, email: w.row.Email, error: `link failed: ${err.message}` });
        }
      } else {
        unresolved++;
      }
      setProgress(p => ({ ...p, done: linked + unresolved, errors }));
    }

    setRunning(false);
    setDoneSummary({
      created: buckets.newRows.length - errors.filter(e => e.error.startsWith('create')).length,
      updated: buckets.matched.length - errors.filter(e => e.error.startsWith('update')).length,
      linked,
      unresolved,
      errors,
    });
  }

  if (loading) return <div style={S.page}><div style={S.loading}>Loading…</div></div>;

  return (
    <div style={S.page}>
      <div style={S.header}>
        <h1 style={S.title}>Roster import</h1>
        <p style={S.sub}>Upload your source-system roster export. Active + Terminated reps will be imported. Existing reps matched by email keep their sales ID.</p>
      </div>

      <div style={S.card}>
        <div style={S.cardTitle}>Step 1 · Upload roster .xlsx</div>
        <div style={S.uploadArea} onClick={() => document.getElementById('roster-input').click()}>
          <div style={S.upIcon}>↑</div>
          <div style={S.upText}>Click to upload Roster_*.xlsx</div>
          <input id="roster-input" type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleFile} />
        </div>
        <div style={S.context}>
          Comp plans in Firestore: <b style={{ color: '#00d4a0' }}>{compPlans.length}</b> · Existing users: <b style={{ color: '#5599cc' }}>{existingUsers.length}</b>
        </div>
      </div>

      {buckets && !running && !doneSummary && (
        <>
          <div style={S.card}>
            <div style={S.cardTitle}>Step 2 · Preview</div>
            <div style={S.stats}>
              <Stat label="Will update" value={buckets.matched.length} color="#f5a623" />
              <Stat label="Will create (new)" value={buckets.newRows.length} color="#00d4a0" />
              <Stat label="Skipped" value={buckets.skipped.length} color="#666" />
              <Stat label="Comp unmatched" value={[...buckets.matched, ...buckets.newRows].filter(r => r._compMissing).length} color="#ff6b6b" />
            </div>
            {buckets.newRows.length > 0 && (
              <details style={S.details}>
                <summary style={S.summary}>{buckets.newRows.length} new reps (will get amber "Needs sales ID" badge)</summary>
                <PreviewTable rows={buckets.newRows.slice(0, 50)} kind="new" />
              </details>
            )}
            {buckets.matched.length > 0 && (
              <details style={S.details}>
                <summary style={S.summary}>{buckets.matched.length} matched reps (sales ID preserved)</summary>
                <PreviewTable rows={buckets.matched.slice(0, 50)} kind="matched" />
              </details>
            )}
            {buckets.skipped.length > 0 && (
              <details style={S.details}>
                <summary style={S.summary}>{buckets.skipped.length} skipped</summary>
                <div style={{ fontSize: 11, color: '#666', padding: '6px 12px' }}>
                  {Object.entries(buckets.skipped.reduce((a, r) => { a[r._reason] = (a[r._reason] || 0) + 1; return a; }, {})).map(([reason, n]) => (
                    <div key={reason}>{reason}: <b>{n}</b></div>
                  ))}
                </div>
              </details>
            )}
          </div>

          <div style={S.actions}>
            <button style={S.btn} onClick={runImport}>
              Import {buckets.matched.length + buckets.newRows.length} reps
            </button>
          </div>
        </>
      )}

      {running && (
        <div style={S.card}>
          <div style={S.cardTitle}>{progress.stage}…</div>
          <div style={S.progressBar}>
            <div style={{ ...S.progressFill, width: `${progress.total ? Math.round(100 * progress.done / progress.total) : 0}%` }} />
          </div>
          <div style={{ fontSize: 13, color: '#888' }}>{progress.done} / {progress.total}</div>
          {progress.errors.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 11, color: '#ff6b6b' }}>
              {progress.errors.slice(-3).map((e, i) => <div key={i}>⚠ {e.name}: {e.error}</div>)}
            </div>
          )}
        </div>
      )}

      {doneSummary && (
        <div style={S.card}>
          <div style={S.cardTitle}>Import complete</div>
          <div style={S.stats}>
            <Stat label="Created" value={doneSummary.created} color="#00d4a0" />
            <Stat label="Updated" value={doneSummary.updated} color="#f5a623" />
            <Stat label="Uplines linked" value={doneSummary.linked} color="#5599cc" />
            <Stat label="Upline unresolved" value={doneSummary.unresolved} color="#666" />
          </div>
          {doneSummary.errors.length > 0 && (
            <details style={S.details}>
              <summary style={S.summary}>{doneSummary.errors.length} errors</summary>
              <div style={{ fontSize: 11, color: '#ff6b6b', padding: '6px 12px', maxHeight: 240, overflowY: 'auto' }}>
                {doneSummary.errors.map((e, i) => <div key={i}>{e.name} ({e.email}): {e.error}</div>)}
              </div>
            </details>
          )}
          <div style={S.nextSteps}>
            Next: go to <b>Reps</b> to find the amber-highlighted entries needing a sales ID.
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={S.stat}>
      <div style={S.statLbl}>{label}</div>
      <div style={{ ...S.statVal, color }}>{value}</div>
    </div>
  );
}

function PreviewTable({ rows, kind }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={S.table}>
        <thead><tr>{['Name', 'Email', 'Role', 'Comp', 'Manager', 'Director', 'RM'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={kind === 'new' ? { background: '#1a1610' } : undefined}>
              <td style={S.td}>{r.Name}</td>
              <td style={{ ...S.td, color: '#666' }}>{r.Email}</td>
              <td style={{ ...S.td, color: '#888' }}>{r._role}</td>
              <td style={{ ...S.td, color: r._compMissing ? '#ff6b6b' : '#00d4a0' }}>{r.Comp || '—'}</td>
              <td style={{ ...S.td, color: '#888' }}>{r.Manager || '—'}</td>
              <td style={{ ...S.td, color: '#888' }}>{r.Director || '—'}</td>
              <td style={{ ...S.td, color: '#888' }}>{r.RM || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const S = {
  page: { padding: '20px 16px', color: '#fff', width: '100%', boxSizing: 'border-box' },
  header: { marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 600, margin: 0, letterSpacing: '-0.3px' },
  sub: { fontSize: 13, color: '#555', margin: '4px 0 0', lineHeight: 1.6 },
  loading: { color: '#555', padding: 40 },
  card: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 12, padding: '20px 24px', marginBottom: 16 },
  cardTitle: { fontSize: 12, color: '#888', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 },
  uploadArea: { border: '1px dashed #2a2a3a', borderRadius: 10, padding: '32px', textAlign: 'center', cursor: 'pointer' },
  upIcon: { fontSize: 28, color: '#00d4a0', marginBottom: 10 },
  upText: { fontSize: 14, color: '#888' },
  context: { marginTop: 12, fontSize: 12, color: '#666' },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 },
  stat: { background: '#0f0f18', borderRadius: 8, padding: '10px 12px' },
  statLbl: { display: 'block', fontSize: 10, color: '#555', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 },
  statVal: { fontSize: 22, fontWeight: 600 },
  details: { marginBottom: 10, background: '#0f0f18', borderRadius: 8, padding: '8px 12px' },
  summary: { fontSize: 13, color: '#aaa', cursor: 'pointer', padding: '4px 0' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: { textAlign: 'left', padding: '6px 10px', color: '#555', fontWeight: 500, borderBottom: '1px solid #1e1e2a', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em' },
  td: { padding: '7px 10px', color: '#ccc', borderBottom: '1px solid #0f0f18' },
  actions: { display: 'flex', gap: 12 },
  btn: { background: '#00d4a0', border: 'none', borderRadius: 8, padding: '12px 32px', color: '#000', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  progressBar: { background: '#1a1a24', borderRadius: 6, height: 6, marginBottom: 8, overflow: 'hidden' },
  progressFill: { background: '#00d4a0', height: '100%', borderRadius: 6, transition: 'width .3s' },
  nextSteps: { marginTop: 12, fontSize: 13, color: '#666', background: '#0f0f18', borderRadius: 8, padding: '12px 16px', lineHeight: 1.6 },
};
